import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, isToday, isThisYear, differenceInDays } from "date-fns"
import { ptBR } from "date-fns/locale"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converte uma data para string no formato YYYY-MM-DD sem problemas de timezone
 * @param date - Data a ser convertida
 * @returns String no formato YYYY-MM-DD
 */
export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converte `YYYY-MM-DD` (input type=date / {@link formatDateForInput}) em ISO para colunas
 * `timestamptz`. Só a data costuma ser interpretada como meia-noite UTC; em America/Sao_Paulo
 * isso vira o dia civil anterior ao exibir. Meio-dia UTC mantém o mesmo dia ao usar
 * {@link formatDateOnlyInSaoPauloMs} / {@link parseDateFromInput}.
 */
export function encodeYmdForTimestamptzColumn(ymd: string): string {
  const s = ymd.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return `${s}T12:00:00.000Z`;
}

/**
 * Data civil (YYYY-MM-DD) em America/Sao_Paulo para um instante — alinhado ao Pipedrive/sync BR.
 */
function formatDateOnlyInSaoPauloMs(ms: number): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ms));
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return y && m && d ? `${y}-${m}-${d}` : "";
}

/**
 * String de data (YYYY-MM-DD ou ISO com hora) → Date local de calendário sem exibir um dia a menos no BR.
 * - Apenas `YYYY-MM-DD`: componentes locais literais.
 * - ISO/datetime: primeiro dia civil em São Paulo, depois Date(y,m,d) local para pickers/exibição.
 */
export function parseDateFromInput(dateString: string): Date {
  const s = String(dateString).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [year, month, day] = s.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const head = s.slice(0, 10);
  if (
    /^\d{4}-\d{2}-\d{2}$/.test(head) &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s) &&
    !s.includes("T")
  ) {
    const [year, month, day] = head.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const normalized = s.includes("T") ? s : s.replace(/^(\d{4}-\d{2}-\d{2}) /, "$1T");
  const ms = Date.parse(normalized);
  if (!Number.isNaN(ms)) {
    const ymd = formatDateOnlyInSaoPauloMs(ms);
    if (ymd) {
      const [year, month, day] = ymd.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
  }
  const head2 = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head2)) {
    const [year, month, day] = head2.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(NaN);
}

const TZ_SP = "America/Sao_Paulo";

/**
 * Exibe data civil (campo tipo date, close_date, custom_data) em dd/MM/yyyy sem deslocar um dia no BR.
 */
export function formatDateOnlyPtBR(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "—";
  const d = value instanceof Date ? value : parseDateFromInput(String(value));
  if (Number.isNaN(d.getTime())) {
    const s = typeof value === "string" ? value.trim() : "";
    return s || "—";
  }
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Formata instante (timestamptz / ISO com hora) no fuso de São Paulo.
 * Se a string for só `YYYY-MM-DD`, delega a {@link formatDateOnlyPtBR} (evita interpretar como UTC meia-noite).
 */
export function formatDateTimeInSaoPaulo(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions
): string {
  if (value == null || value === "") return "—";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "—";
    return value.toLocaleString("pt-BR", { timeZone: TZ_SP, ...options });
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return formatDateOnlyPtBR(s);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("pt-BR", { timeZone: TZ_SP, ...options });
}

/** Só a parte da data (dia/mês/ano) de um instante, em São Paulo — ex.: created_at. */
export function formatInstantDatePtBR(value: string | Date | null | undefined): string {
  return formatDateTimeInSaoPaulo(value, { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Data e hora curtas em São Paulo — ex.: logs, integrações. */
export function formatInstantDateTimePtBR(value: string | Date | null | undefined): string {
  return formatDateTimeInSaoPaulo(value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Apenas hora:minuto em São Paulo a partir de um ISO/instante. */
function formatInstantTimePtBR(value: string | Date | null | undefined): string {
  return formatDateTimeInSaoPaulo(value, { hour: "2-digit", minute: "2-digit" });
}

/** Diferença em dias entre duas datas civis (meia-noite local de cada uma). */
export function diffCalendarDays(a: Date, b: Date): number {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ua - ub) / 86400000);
}

/**
 * Atividade está atrasada?
 * Sem horário (meia-noite local) → compara só o dia (atrasada se dia < hoje).
 * Com horário → compara timestamp completo.
 */
export function isActivityOverdue(dateStr: string, now: Date = new Date()): boolean {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const noTime = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  if (noTime) return diffCalendarDays(d, now) < 0;
  return d.getTime() < now.getTime();
}

/**
 * Formata o título da atividade para incluir apenas o tipo
 * Ex: "Atividade de Ligação"
 */
function formatActivityTitle(title: string, type: string): string {
  const typeLabels: Record<string, string> = {
    email: 'Email',
    call: 'Ligação',
    meeting: 'Reunião',
    task: 'Tarefa',
    note: 'Nota',
    whatsapp: 'WhatsApp',
  };

  const typeLabel = typeLabels[type] || 'Atividade';
  return `Atividade de ${typeLabel}`;
}

/**
 * Formata o horário para exibição em conversas e mensagens
 * - Hoje: exibe apenas o horário (ex: "12:35")
 * - Até uma semana: exibe o dia da semana (ex: "Quarta-feira")
 * - Mais de uma semana, mesmo ano: exibe dia/mês (ex: "27/12")
 * - Ano anterior: exibe data completa (ex: "27/12/2025")
 * @param date - Data/hora a ser formatada
 * @returns String formatada conforme as regras
 */
/**
 * Gera uma cor HSL determinística a partir de uma string (nome do usuário).
 * Mesmo nome = mesma cor sempre.
 */
export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  const sat = 45 + (Math.abs(hash >> 8) % 20);
  const light = 40 + (Math.abs(hash >> 16) % 15);
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

/**
 * Formata data relativa em português (agora, 5min, 3h, 2d, 27 mar)
 */
export function formatRelativeTime(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return `ontem`;
  if (diffDays < 7) return `${diffDays}d`;
  return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export function formatChatTime(date: string | Date): string {
  const messageDate = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const daysDiff = differenceInDays(now, messageDate);
  
  // Hoje: exibir apenas horário
  if (isToday(messageDate)) {
    return format(messageDate, 'HH:mm', { locale: ptBR });
  }
  
  // Ontem
  if (daysDiff === 1) {
    return 'Ontem';
  }

  // Esta semana: dia abreviado + hora ("Sex 14:32")
  if (daysDiff <= 7) {
    const dayAbbr = format(messageDate, 'EEE', { locale: ptBR });
    const time = format(messageDate, 'HH:mm', { locale: ptBR });
    return `${dayAbbr.charAt(0).toUpperCase() + dayAbbr.slice(1)} ${time}`;
  }

  // Mesmo ano: "28 Mar"
  if (isThisYear(messageDate)) {
    return format(messageDate, "dd MMM", { locale: ptBR });
  }

  // Ano anterior: "28 Mar 25"
  return format(messageDate, "dd MMM yy", { locale: ptBR });
}

/**
 * Escapa `\`, `%` e `_` no termo digitado para uso seguro em filtros PostgREST
 * `.ilike(\`%${escapeForIlikePattern(term)}%\`)` (evita wildcard acidental).
 */
export function escapeForIlikePattern(term: string): string {
  return term.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}